import type { EstimateConfidenceLevel } from '@lizrd/contracts';
import { usd } from '../../lib/graph-visuals';
import type { CostProvenance } from '../../lib/cost-truth';
import { CONFIDENCE_META, confidenceChipLabel } from '../EstimateConfidenceModal';

export type { CostProvenance };

interface CostProps {
  /** The dollar amount. Null/0 renders as an em-dash with no tag. */
  value: number | null | undefined;
  /** `billed` = the cloud provider's actual charges; `estimated` = our reconciled estimate. */
  provenance: CostProvenance;
  /** Confidence band for the estimate fallback (drives the dot + label). */
  confidence?: EstimateConfidenceLevel;
  /** Append "/mo" to the amount. */
  monthly?: boolean;
  currency?: string;
  /** Opens the estimate-confidence explainer when the estimated tag is clicked. */
  onExplain?: () => void;
  /** Full label ("Est · High confidence") vs the compact tag ("Est."). */
  detailed?: boolean;
  /** className for the amount itself (font size/weight comes from the caller). */
  className?: string;
  /** Render just the amount, no provenance tag. */
  showTag?: boolean;
  /** For a billed calendar-month figure, the projected FULL-month total. When
   * present (and provenance is billed), renders "· ~$Y proj" next to the
   * month-to-date amount. */
  projected?: number | null;
}

const TAG =
  'inline-flex items-center gap-1 rounded-full border border-hairline px-1.5 py-0.5 text-[0.6rem] font-semibold uppercase tracking-wide';

/**
 * A cost/spend figure that carries its provenance: it leads with the real BILLED
 * amount when we have it and clearly marks an ESTIMATE (with its confidence)
 * otherwise. The single place the app renders money, so billed-vs-estimated reads
 * consistently across the Dashboard, Domain Explorer, and every cost surface.
 */
export function Cost({
  value,
  provenance,
  confidence,
  monthly,
  currency,
  onExplain,
  detailed,
  className,
  showTag = true,
  projected,
}: CostProps) {
  const has = typeof value === 'number' && value > 0;
  const amount = has ? `${usd(value, currency)}${monthly ? '/mo' : ''}` : '—';
  // Projected run-rate shows for any provenance (billed month-to-date or estimate).
  const hasProjected = typeof projected === 'number' && projected > 0 && has;
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <span className={className}>{amount}</span>
      {hasProjected && (
        <span
          className="text-sm font-normal text-muted"
          title="Run rate for the full month (projected). We bill on the settled month."
        >
          · ~{usd(projected!, currency)} run rate
        </span>
      )}
      {/* Billed cost carries no pill — it's the default. Only an ESTIMATE is flagged. */}
      {has && showTag && provenance === 'estimated' && (
        <EstimatedTag confidence={confidence} detailed={detailed} onExplain={onExplain} />
      )}
    </span>
  );
}

function EstimatedTag({
  confidence,
  detailed,
  onExplain,
}: {
  confidence?: EstimateConfidenceLevel;
  detailed?: boolean;
  onExplain?: () => void;
}) {
  const dot = confidence ? CONFIDENCE_META[confidence].dot : 'var(--muted)';
  const label = detailed && confidence ? `Est · ${confidenceChipLabel(confidence)}` : 'Est.';
  const inner = (
    <>
      <span
        className="h-1.5 w-1.5 rounded-full"
        style={{ backgroundColor: dot }}
        aria-hidden="true"
      />
      {label}
    </>
  );
  return onExplain ? (
    <button
      type="button"
      onClick={onExplain}
      className={`${TAG} text-muted transition-colors hover:bg-surface-2`}
      title="Estimated — click to see how confident"
    >
      {inner}
    </button>
  ) : (
    <span className={`${TAG} text-muted`} title="Estimated cost">
      {inner}
    </span>
  );
}
